import React, {useRef} from 'react';
import clsx from 'clsx';
import {type HeaderGroup, type RowData} from '@tanstack/react-table';
import {type ColumnPinningState} from '@tanstack/table-core';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {
	getTableHeaderUtilityClass,
	TABLE_HEADER_CLASS_NAME,
	tableHeaderClasses,
	TableHeaderClasses,
} from './tableHeaderClasses';
import {TableHeaderRow} from './TableHeaderRow';

type TableHeaderOwnerState = WithClasses<TableHeaderClasses>;

const useUtilityClasses = (ownerState: TableHeaderOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getTableHeaderUtilityClass, classes);
};

const TableHeaderRoot = styled('div', {
	name: TABLE_HEADER_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	flexWrap: 'nowrap',
	background: theme.palette.background.paper,
	borderBottom: `1px solid ${theme.palette.custom.border}`,
	...theme.mixins.positionSticky,
	left: 0,
	top: 0,
	zIndex: 3,
}));

export interface TableHeaderProps<TData extends RowData>
	extends WithClasses<TableHeaderClasses> {
	disableFocus?: boolean;
	disabled?: boolean;
	headerGroups: HeaderGroup<TData>[];
	height?: number;
	lastSizeableColumnIndex?: number;
	leftLeafColumnWidths?: number[];
	pinnedColumns?: ColumnPinningState;
	rightLeafColumnWidths?: number[];
	rowWidth?: number;
}

export const TableHeaderPrimitive = <TData extends RowData>(
	{
		classes: _classes,
		className,
		disableFocus,
		disabled,
		headerGroups,
		height,
		lastSizeableColumnIndex,
		leftLeafColumnWidths,
		pinnedColumns,
		rightLeafColumnWidths,
		rowWidth,
		...props
	}: TableHeaderProps<TData>,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	const columnsWrapperRef = useRef<HTMLDivElement>(null);

	const ownerState: TableHeaderOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<TableHeaderRoot
			role="rowgroup"
			ref={ref}
			className={clsx(classes.root, className)}
			{...props}
			style={{width: rowWidth}}
		>
			{headerGroups.map((headerGroup, index) => (
				<TableHeaderRow
					ref={columnsWrapperRef}
					disabled={disabled}
					index={index}
					key={headerGroup.id}
					headerGroup={headerGroup}
					height={height}
					lastSizeableColumnIndex={lastSizeableColumnIndex}
					leftLeafColumnWidths={leftLeafColumnWidths}
					pinnedColumns={pinnedColumns}
					rightLeafColumnWidths={rightLeafColumnWidths}
				/>
			))}
		</TableHeaderRoot>
	);
};

export const TableHeader = React.forwardRef(TableHeaderPrimitive) as <
	TData extends RowData,
>(
	props: TableHeaderProps<TData> & React.RefAttributes<HTMLDivElement>,
) => JSX.Element;

export default TableHeader;
